import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "UL", "OL", "LI", "BLOCKQUOTE", "PRE", "HR", "TABLE"]);

/**
 * Zet losse tekst en inline-elementen op het hoogste niveau in een <p> en
 * zet een top-level <div> om naar <p>. Zo is elke alinea een eigen <p> met
 * eigen witruimte, ook bij oudere inhoud of plaksel van buitenaf.
 */
function normalizeAlineas(html) {
    const host = document.createElement("div");
    host.innerHTML = html || "";
    const frag = document.createDocumentFragment();
    let buffer = [];
    const flush = () => {
        const meaningful = buffer.filter(
            (n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent || "").trim() !== ""
        );
        if (meaningful.length) {
            const p = document.createElement("p");
            meaningful.forEach((n) => p.appendChild(n));
            frag.appendChild(p);
        }
        buffer = [];
    };
    Array.from(host.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "DIV") {
            flush();
            const p = document.createElement("p");
            while (node.firstChild) p.appendChild(node.firstChild);
            frag.appendChild(p);
        } else if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName)) {
            flush();
            frag.appendChild(node);
        } else {
            buffer.push(node);
        }
    });
    flush();
    const out = document.createElement("div");
    out.appendChild(frag);
    return out.innerHTML;
}

/**
 * Eenvoudige richtext-editor. Basis: alinea, vet, cursief, link, opsomming.
 * Met full=true ook: kop H2, kop H3 en foto in de tekst (upload + alt).
 * De server sanitizeert de HTML bij opslaan.
 */
export default function RichTextEditor({ value, onChange, testid, full = false }) {
    const ref = useRef(null);
    const mounted = useRef(false);
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!mounted.current && ref.current) {
            // Enter maakt een nieuwe <p>, Shift+Enter een <br>. Zonder dit
            // maakt de browser <div>-blokken die de server-sanitizer stript,
            // waardoor alinea's aan elkaar plakken.
            document.execCommand("defaultParagraphSeparator", false, "p");
            // Een lege editor begint met <p><br></p>, zodat de eerste getypte
            // regel meteen in een alinea staat. Bestaande inhoud wordt
            // genormaliseerd, zodat losse tekst ook een eigen <p> krijgt.
            const start = normalizeAlineas(value || "");
            ref.current.innerHTML = start || "<p><br></p>";
            mounted.current = true;
        }
    }, [value]);

    const emit = () => onChange(normalizeAlineas(ref.current?.innerHTML || ""));

    const exec = (cmd, arg = null) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        emit();
    };

    const addLink = () => {
        const url = window.prompt("Linkadres (https://, mailto: of tel:)");
        if (url) exec("createLink", url);
    };

    const addImage = async (file) => {
        setError("");
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            setError("Bestand is te groot (max 8 MB).");
            return;
        }
        // eerst de alt-tekst vragen, zodat annuleren geen wees-upload achterlaat
        const alt = window.prompt("Alt-tekst voor de foto (verplicht):");
        if (!alt) {
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axios.post(`${API}/admin/media`, fd, { withCredentials: true });
            ref.current?.focus();
            document.execCommand(
                "insertHTML",
                false,
                `<img src="${r.data.url}" alt="${alt.replace(/"/g, "&quot;")}"/>`
            );
            emit();
        } catch (err) {
            setError(err.response?.data?.detail || "Uploaden is mislukt.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const tools = [
        ["Alinea", () => exec("formatBlock", "p"), "rt-p"],
        ...(full
            ? [
                  ["Kop H2", () => exec("formatBlock", "h2"), "rt-h2"],
                  ["Kop H3", () => exec("formatBlock", "h3"), "rt-h3"],
              ]
            : []),
        ["Vet", () => exec("bold"), "rt-bold"],
        ["Cursief", () => exec("italic"), "rt-italic"],
        ["Link", addLink, "rt-link"],
        ["Opsomming", () => exec("insertUnorderedList"), "rt-list"],
        ...(full
            ? [["Foto", () => fileRef.current?.click(), "rt-image"]]
            : []),
    ];

    return (
        <div data-testid={testid}>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {tools.map(([label, fn, id]) => (
                    <button
                        key={id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={fn}
                        disabled={uploading}
                        data-testid={`${id}-${testid}`}
                        className="rounded-full border border-[var(--hds-line)] bg-[var(--hds-sand)] px-3 py-1 text-[12px] text-[var(--hds-ink)]/70 hover:border-[var(--hds-clay)] transition-colors duration-300 disabled:opacity-50"
                    >
                        {label}
                    </button>
                ))}
            </div>
            {full && (
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => addImage(e.target.files?.[0])}
                    className="hidden"
                    data-testid={`rt-image-input-${testid}`}
                />
            )}
            {uploading && <p className="text-[12px] text-[var(--hds-ink)]/50 mb-1">Foto uploaden...</p>}
            {error && <p className="text-[12.5px] text-[var(--hds-clay-dark)] mb-1" data-testid={`rt-error-${testid}`}>{error}</p>}
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                data-testid={`${testid}-area`}
                className="min-h-[140px] rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[14.5px] leading-[1.6] transition-colors duration-300"
            />
        </div>
    );
}
